import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BookedMeeting, CalEventInterface } from "../../interfaces/common";
import {Modal, ModalHeader, ModalBody, ModalFooter} from "../Modal";
import CustomButton from "../CustomButton";
import Checkbox from "../Checkbox";
import {
  addMinutes, convertToWeekDate,
  dateFormatDate,
  dateFormatHourMinute, twoDigits,
} from "../../helpers/dateFunctions";
import iconCalendar from "url:shared-assets/icons/calendar.svg";
import iconClock from "url:shared-assets/icons/clock.svg";
import iconBadgeCheck from "url:shared-assets/icons/badge-check.svg";
import iconClose from "url:shared-assets/icons/close.svg";
import Api from "../../services/Api";
import CalendarBig from "../CalendarBig";

interface Props {
  meeting?: BookedMeeting;
  clientId: string;
  close: Function;
  lang: string;
  remainingCredits?: number;
}

const BookMeetingFree: React.FC<Props> = ({ meeting, clientId, close, lang, remainingCredits }) => {
  const api = Api.getInstance();
  const translation = useTranslation();
  const { t } = translation;
  const [txt, setTxt] = useState<string>("");
  const [booked, setBooked] = useState<boolean>(false);
  const [confirm, setConfirm] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [newStartTime, setNewStartTime] = useState<Date | null>(null);
  const [avails, setAvails] = useState<Array<CalEventInterface>>([]);
  const [clientAgreed, setClientAgreed] = useState<boolean>(false);
  const [newHour, setNewHour] = useState(0);
  const [newMinute, setNewMinute] = useState(0);
  const today = new Date();
  const hours = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
  const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
  const [meetingLength, setMeetingLength] = useState<number>(meeting ? meeting.length : 30);
  const [meetingLengths, setMeetingLengths] = useState<Array<number>>([]);
  const remainingMinutes = remainingCredits ? remainingCredits : meeting ? meeting.remainingCredits : null;

  useEffect(() => {
    if (remainingMinutes) {
      const _meetingLengths = []
      for (const l of [30, 45, 60, 90]) {
        if (l <= remainingMinutes) {
          _meetingLengths.push(l);
        }
      }
      setMeetingLengths(_meetingLengths);
    }
    api.get('coach/availability/get?summary=1').then((result) => {
      const availableEvents: Array<CalEventInterface> = []

      let id = 1
      for (const available of result.json.available) {
        let from = new Date(available.from)
        let to = new Date(available.to)
        if (available.title === 'R') {
          from = convertToWeekDate(today, from)
          to = convertToWeekDate(today, to)
        }
        availableEvents.push({
          id: id,
          dbId: available.id,
          title: available.title,
          start: from,
          end: to,
        })
        id++
      }

      setAvails(availableEvents)
      setLoaded(true)
    })
  }, []);

  const doBook = () => {
    let changedTime = newStartTime;
    if (!changedTime || !meeting) return;
    changedTime.setHours(newHour)
    changedTime.setMinutes(newMinute)
    const data = {
      id: meeting.id,
      client_id: clientId,
      from: changedTime,
      length: meetingLength,
      message: txt,
    };
    api.post("coach/meeting/book", data).then((response) => {
      if (response.json.error) {
        alert(response.json.error);
      } else {
        setBooked(true);
      }
    });
  };

  const showContent = () => {
    if (booked) {
      return (
        <div>
          <div className="my-12 flex justify-center items-center">
            <img className="icon-xl" alt="check" src={iconBadgeCheck} />
          </div>
          <div className="text-neutral-300">
            {t("your-meeting-has")}{" "}
            {meeting?.id === "0" ? t("been-booked") : t("been-changed")}{" "}
            {t("client-is-notified")}
          </div>
        </div>
      );
    } else if (newStartTime) {
      return (
        <div>
          <div className="font-medium">
            {meeting?.firstName} {meeting?.lastName}
          </div>
          <div className="mt-4 flex items-center">
            <img alt="calendar" className="icon" src={iconCalendar} />
            <div className="ml-2">
              {dateFormatDate(newStartTime, false, lang)}
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <img className="icon" alt="Time" src={iconClock}/>
            <div className="ml-2 flex">
              <select value={newHour} className="z-select sm"
                      onChange={(e) => setNewHour(parseInt(e.target.value))}>
                {hours.map(h => (
                  <option key={"hour_" + h} value={h}>{twoDigits(h)}</option>
                ))}
              </select>&nbsp;:&nbsp;
              <select value={newMinute} className="z-select sm"
                      onChange={(e) => setNewMinute(parseInt(e.target.value))}>
                {minutes.map(m => (
                  <option key={"minute_" + m} value={m}>{twoDigits(m)}</option>
                ))}
              </select>
            </div>
            <div className="ml-4">{lang === "sv" ? "Möteslängd" : "Meeting length"}:</div>
            {
              meetingLengths.length > 1 ? (
                <div className="ml-2">
                  <select value={meetingLength} className="z-select sm"
                          onChange={(e) => setMeetingLength(parseInt(e.target.value))}>
                    {meetingLengths.map(m => (
                      <option key={"l_" + m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="ml-2">
                  {meetingLength}
                </div>
              )
            }
            <div className="ml-1">min</div>

          </div>

          <div className="mt-4 flex items-center">
            <div onClick={() => setClientAgreed(!clientAgreed)}>
              <Checkbox checked={clientAgreed} id="_agreed"
                        label={lang === "sv" ? "Avstämt med klient" : "Confirmed with client"}/>
            </div>
          </div>

          <div className="mt-4">
            <textarea
              value={txt}
              className="z-input z-700 h-24"
              placeholder={t("write-message-to-client")}
              onChange={(e) => setTxt(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>
      );
    } else {
      return <></>;
    }
  };

  useEffect(() => {
    if (newStartTime) {
      setNewHour(newStartTime.getHours());
      setNewMinute(newStartTime.getMinutes());
      setConfirm(true);
    }
  }, [newStartTime]);

  const showConfirmText = () => {
    if (booked) {
      if (meeting?.id === "0") {
        if (lang === "sv") {
          return "Mötet har bokats";
        } else {
          return "The meeting has been booked";
        }
      } else {
        if (lang === "sv") {
          return "Mötet har ändrats";
        } else {
          return "The meeting has been changed";
        }
      }
    } else {
      if (meeting?.id === "0") {
        if (lang === "sv") {
          return "Bekräfta bokning";
        } else {
          return "Confirm meeting";
        }
      } else {
        if (lang === "sv") {
          return "Bekräfta ändring";
        } else {
          return "Confirm change";
        }
      }
    }
  };

  const clickTime = (start: Date) => {
    if (start < today) {
      alert("You can't set a start time that has already passed");
    } else {
      setNewStartTime(start);
    }
  }

  return (
    <>
      <Modal isOpen={confirm} onClose={() => setConfirm(false)}>
        <ModalHeader onClose={() => setConfirm(false)}>
          {showConfirmText()}
        </ModalHeader>
        <ModalBody>
          <>{showContent()}</>
        </ModalBody>
        <ModalFooter>
          {booked ? (
            <CustomButton className="w-full" onClick={() => close(booked)}>
              Ok
            </CustomButton>
          ) : (
            <CustomButton
              className="w-full"
              onClick={() => doBook()}
              disabled={!clientAgreed}
            >
              {meeting?.id === "0" ? t("book-meeting") : t("change-meeting")}
            </CustomButton>
          )}
        </ModalFooter>
      </Modal>

      {(meeting && meeting.id != "0") ? (
        <>
          <div className="text-2xl mt-4 font-medium">
            {lang === "sv" ? "Ändra datum och tid" : "Change date and time"}
          </div>
          <div className="mt-4 p-4 bg-neutral-700 text-white rounded border border-neutral-500">
            <div className="flex justify-betwen items-center">
              <div className="font-medium">{t("original-booking")}</div>
              <div className="flex flex-grow justify-end">
                <img
                  className="cursor-pointer"
                  src={iconClose}
                  alt="close"
                  onClick={() => close(false)}
                />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <div>
                {meeting.firstName} {meeting.lastName}
              </div>
              <div className="ml-4 flex">
                <img src={iconCalendar} className="icon" alt="cal" />
                <span className="ml-1">
                  {dateFormatDate(meeting.startDate, false, lang)}
                </span>
              </div>
              <div className="ml-4 flex">
                <img src={iconClock} className="icon" alt="cal" />
                <span className="ml-1">
                  {dateFormatHourMinute(meeting.startDate)} -{" "}
                  {dateFormatHourMinute(
                    addMinutes(meeting.startDate, meeting.length)
                  )}
                </span>
              </div>

            </div>
            <div className="mt-4">
              {lang === "sv" ? "Klicka i kalendern för att välja ny tid" : "Click in the calendar to select new time"}
            </div>
          </div>
          <div className="mt-4">&nbsp;</div>
        </>
        ) : (
        <>
          <div className="text-2xl mt-4 font-medium">
            {lang === "sv" ? "Boka nytt möte" : "Book new meeting"}
          </div>
          <div className="mt-4 p-4 bg-neutral-700 text-white rounded border border-neutral-500">
            <div className="flex justify-betwen items-center">
              <div className="font-medium">{meeting?.firstName} {meeting?.lastName}</div>
              <div className="flex flex-grow justify-end">
                <img
                  className="cursor-pointer"
                  src={iconClose}
                  alt="close"
                  onClick={() => close(false)}
                />
              </div>
            </div>
            <div className="mt-4">
              {lang === "sv" ? "Klicka i kalendern för att välja tid" : "Click in the calendar to select time"}
            </div>
          </div>
          <div className="mt-4">&nbsp;</div>
        </>
        )
      }

      {
        loaded ? (
            <CalendarBig key="cal_r" startEvents={avails} save={false} calType="book_meeting_free" clickTime={clickTime}
                         calendarHeight={window.innerHeight - 360}/>
          ) : (
            <div>{lang === "sv" ? "Laddar kalender..." : "Loading calendar..."}</div>
        )

      }

    </>
  );
};

export default BookMeetingFree;
